import { useState } from "react";
import {
  Box,
  Container,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";

// Custom icons for each category
const FlexibilityIcon = () => (
  <Box
    width="60px"
    height="60px"
    bg="linear-gradient(135deg, #4299E1 0%, #63B3ED 100%)"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    position="relative"
  >
    <Box
      width="30px"
      height="30px"
      bg="white"
      borderRadius="md"
      position="relative"
    >
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width="20px"
        height="20px"
        bg="orange.400"
        borderRadius="sm"
      />
    </Box>
  </Box>
);

const DiversityIcon = () => (
  <Box
    width="60px"
    height="60px"
    bg="linear-gradient(135deg, #805AD5 0%, #9F7AEA 100%)"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    position="relative"
  >
    <HStack spacing={1}>
      <Box width="8px" height="8px" bg="white" borderRadius="full" />
      <Box width="8px" height="8px" bg="yellow.400" borderRadius="full" />
      <Box width="8px" height="8px" bg="green.400" borderRadius="full" />
      <Box width="8px" height="8px" bg="pink.400" borderRadius="full" />
      <Box width="8px" height="8px" bg="blue.400" borderRadius="full" />
    </HStack>
  </Box>
);

const SupportIcon = () => (
  <Box
    width="60px"
    height="60px"
    bg="linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    position="relative"
  >
    <Box
      width="30px"
      height="30px"
      bg="white"
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box width="16px" height="16px" bg="purple.500" borderRadius="full" />
    </Box>
  </Box>
);

const GrowthIcon = () => (
  <Box
    width="60px"
    height="60px"
    bg="linear-gradient(135deg, #F56565 0%, #FC8181 100%)"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    position="relative"
  >
    <VStack spacing={0}>
      <Box width="4px" height="8px" bg="white" />
      <Box width="8px" height="6px" bg="white" />
      <Box width="12px" height="4px" bg="white" />
      <Box width="16px" height="2px" bg="white" />
    </VStack>
  </Box>
);

// Check Icon Component
const CheckIcon = () => (
  <Box
    width="120px"
    height="120px"
    bg="linear-gradient(135deg, #48BB78 0%, #68D391 100%)"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    boxShadow="0 10px 30px rgba(72, 187, 120, 0.3)"
  >
    {/* <Box
      width="60px"
      height="60px"
      bg="white"
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
    > */}
      {/* Checkmark SVG */}
      <svg
        width="120"
        height="120"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 12L11 14L15 10"
          stroke="White"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    {/* </Box> */}
  </Box>
);

// Thank You Component
const ThankYouScreen = ({ selectedOption, onReset, thankYouMessage, surveyQuestion }) => {
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  return (
    <VStack spacing={8} textAlign="center">
      {/* Title */}
      <Text
        fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        color={textColor}
        fontWeight="bold"
        lineHeight="1.2"
      >
        {surveyQuestion}
      </Text>

      {/* Subtitle */}
      <Text
        fontSize={{ base: "lg", md: "xl" }}
        color={subtitleColor}
        lineHeight="1.6"
      >
        {thankYouMessage}
      </Text>

      {/* Check Icon - positioned under title and subtitle */}
      <CheckIcon />

    </VStack>
  );
};

const WorkplaceCultureSurvey = ({ surveyData = null }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const selectedBorderColor = useColorModeValue("#000B8C", "blue.300");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");

  const gridColumns = useBreakpointValue({
    base: 1,
    sm: 2,
    md: 2,
    lg: 4,
  });

  const cardPadding = useBreakpointValue({
    base: 6,
    md: 8,
  });

  // Use dynamic data if available, otherwise fallback to static data
  const surveyQuestion = surveyData?.surveyQuestion || "What's Most Important in Workplace Culture?";
  const surveySubtitle = surveyData?.surveySubtitle || "Help us understand what matters most to you";
  const thankYouMessage = surveyData?.thankYouMessage || "Thank you for your Voting";

  // Icon mapping for dynamic data
  const iconComponents = {
    FlexibilityIcon,
    DiversityIcon,
    SupportIcon,
    GrowthIcon
  };

  // Use only dynamic options from CMS
  const options = surveyData?.options?.length > 0 ? surveyData.options.map(option => ({
    ...option,
    icon: iconComponents[option.icon] || FlexibilityIcon
  })) : [];

  // If no survey data is available, return empty state
  if (!surveyData || options.length === 0) {
    return (
      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        <VStack spacing={8} textAlign="center">
          <Text
            fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
            color={textColor}
            fontWeight="bold"
          >
            Workplace Culture Survey
          </Text>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color={subtitleColor}
          >
            Survey content is being updated. Please check back later.
          </Text>
        </VStack>
      </Container>
    );
  }

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
    setShowThankYou(true);
  };

  // const handleReset = () => {
  //   setSelectedOption(null);
  //   setShowThankYou(false);
  // };

  return (
    <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
      <VStack spacing={{ base: 8, md: 12 }}>
        {showThankYou ? (
          <ThankYouScreen
            selectedOption={selectedOption}
            thankYouMessage={thankYouMessage}
            surveyQuestion={surveyQuestion}
            // onReset={handleReset}
          />
        ) : (
          <>
            <Box textAlign="center" maxW="2xl">
              <Text
                fontSize={{ base: "xl", md: "2xl", lg: "4xl" }}
                color={textColor}
                fontWeight="bold"
                mb={4}
                lineHeight="1.2"
              >
                {surveyQuestion}
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color={subtitleColor}
                lineHeight="1.6"
              >
                {surveySubtitle}
              </Text>
            </Box>

            <SimpleGrid
              columns={gridColumns}
              spacing={{ base: 4, md: 6 }}
              width="100%"
              maxW="6xl"
            >
              {options.map((option) => {
                const IconComponent = option.icon;
                const isSelected = selectedOption === option.id;

                return (
                  <Box
                    key={option.id}
                    as="button"
                    onClick={() => handleOptionSelect(option.id)}
                    bg={bgColor}
                    border="2px solid"
                    borderColor={isSelected ? selectedBorderColor : borderColor}
                    borderRadius="xl"
                    p={cardPadding}
                    transition="all 0.3s ease"
                    cursor="pointer"
                    _hover={{
                      borderColor: selectedBorderColor,
                      transform: "translateY(-2px)",
                      shadow: "lg",
                    }}
                    _active={{
                      transform: "translateY(0px)",
                    }}
                    position="relative"
                    overflow="hidden"
                  >
                    {isSelected && (
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height="4px"
                        bg={selectedBorderColor}
                      />
                    )}

                    <VStack spacing={4}>
                      <Box
                        transform={isSelected ? "scale(1.1)" : "scale(1)"}
                        transition="transform 0.2s"
                      >
                        <IconComponent />
                      </Box>

                      <Text
                        fontSize={{ base: "lg", md: "xl" }}
                        fontWeight="semibold"
                        color={textColor}
                      >
                        {option.title}
                      </Text>

                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        textAlign="center"
                        display={{ base: "none", md: "block" }}
                      >
                        {option.description}
                      </Text>
                    </VStack>
                  </Box>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </VStack>
    </Container>
  );
};

export default WorkplaceCultureSurvey;
